'use client';

import {useState, useMemo, forwardRef} from 'react';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {Button} from '../atoms/Button';
import {Typography} from '../atoms/Typography';
import {Giphy} from '../molecules/Giphy';
import {UploadIcon} from '../svg/UploadIcon';
import {CloseLineIcon} from '../svg/CloseLineIcon';
import {charcoal} from '../sass/base/_exports.sass';

export const PhotoOrGifModal = forwardRef(
  (
    {
      className = '',
      processing = false,
      onHandleClose = () => {},
      onChangeImage = () => {},
      isGiphy,
    },
    ref
  ) => {
    const [uploadActive, setUploadActive] = useState(!isGiphy);

    const header = useMemo(
      () => (
        <div className="photo-gif-modal__header">
          <Button
            variant="unstyled-button"
            className="photo-gif-modal__header__close"
            data-qa-id="upload-modal-close-btn"
            onClick={onHandleClose}
            aria-label="Close modal"
          >
            <CloseLineIcon ratio={0.75} />
          </Button>
          <Button
            className={cx({
              'photo-or-gif-modal__button--active': uploadActive,
            })}
            variant="unstyled-button"
            data-qa-id="upload-modal-upload-nav"
            onClick={() => setUploadActive(true)}
          >
            <Typography variant="label2" color={uploadActive ? 'charcoal' : 'pebble'}>
              Upload
            </Typography>
          </Button>
          <Button
            className={cx('photo-or-gif-modal__button', {
              'photo-or-gif-modal__button--active': !uploadActive,
            })}
            variant="unstyled-button"
            data-qa-id="upload-modal-giphy-nav"
            data-impression-tracking="true"
            onClick={() => setUploadActive(false)}
          >
            <Typography variant="label2" color={uploadActive ? 'pebble' : 'charcoal'}>
              Browse Giphy
            </Typography>
          </Button>
        </div>
      ),
      [uploadActive, setUploadActive]
    );

    const uploadContent = useMemo(
      () =>
        uploadActive ? (
          <Button
            variant="unstyled-button"
            className="photo-gif-modal__upload"
            data-qa-id="upload-modal-upload-here"
            onClick={() => ref.current.click()}
          >
            <UploadIcon color={charcoal} />
            <Typography variant="label2">Upload your photo here</Typography>
          </Button>
        ) : (
          <Giphy onHandleGifSelect={onChangeImage} />
        ),
      [uploadActive, onChangeImage]
    );

    return createPortal(
      <div className="photo-gif-modal__wrapper">
        <Button
          variant="unstyled-button"
          className="photo-gif-modal__overlay"
          onClick={onHandleClose}
          data-qa-id="photo-gif-modal-overlay"
        />
        <div className={cx('photo-gif-modal', {[className]: className})}>
          {header}
          <div className="photo-gif-modal__body">
            {processing ? (
              <div className="photo-gif-modal__loading">
                <span className="evt-spinner" />
              </div>
            ) : (
              uploadContent
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }
);

PhotoOrGifModal.propTypes = {
  className: PropTypes.string,
  processing: PropTypes.bool,
  onHandleClose: PropTypes.func,
  onChangeImage: PropTypes.func,
  isGiphy: PropTypes.bool,
};
